export type PersonSecondaryLineEmployment = {
  isPrimary?: boolean | null;
  facility?: { id?: string | null; name?: string | null } | null;
} | null;

export type PersonSecondaryLineSource = {
  primaryFacility?: { id?: string | null; name?: string | null } | null;
  primarySection?: { name?: string | null } | null;
  employments?: PersonSecondaryLineEmployment[] | null;
  facilityName?: string | null;
  sectionName?: string | null;
  otherFacilityCount?: number | null;
};

function trimName(value: string | null | undefined): string | undefined {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
}

export function otherFacilityCountFromEmployments(
  primaryFacilityId: string | null | undefined,
  employments: PersonSecondaryLineEmployment[] | null | undefined
): number {
  const ids = new Set<string>();
  for (const employment of employments ?? []) {
    const id = employment?.facility?.id;
    if (id) ids.add(id);
  }
  if (primaryFacilityId) {
    ids.delete(primaryFacilityId);
    return ids.size;
  }
  return Math.max(0, ids.size - 1);
}

function resolvedFacilityName(source: PersonSecondaryLineSource): string | undefined {
  return (
    trimName(source.primaryFacility?.name) ??
    trimName(source.employments?.find((employment) => employment?.isPrimary)?.facility?.name) ??
    trimName(source.facilityName)
  );
}

function resolvedSectionName(source: PersonSecondaryLineSource): string | undefined {
  return trimName(source.primarySection?.name) ?? trimName(source.sectionName);
}

function resolvedOtherFacilityCount(source: PersonSecondaryLineSource): number {
  if (source.otherFacilityCount != null) return source.otherFacilityCount;
  const primaryId =
    source.primaryFacility?.id ??
    source.employments?.find((employment) => employment?.isPrimary)?.facility?.id ??
    undefined;
  return otherFacilityCountFromEmployments(primaryId, source.employments);
}

export function formatPersonSecondaryLine(source: PersonSecondaryLineSource): string | undefined {
  const facilityName = resolvedFacilityName(source);
  const otherCount = resolvedOtherFacilityCount(source);
  const facilityPart = facilityName ? (otherCount > 0 ? `${facilityName} (+${otherCount})` : facilityName) : undefined;
  const sectionName = resolvedSectionName(source);
  const line = [facilityPart, sectionName].filter(Boolean).join(" · ");
  return line || undefined;
}
